/**
 * TanStack Query key factories.
 * All query keys must go through these factories - never inline an array.
 */

export const queryKeys = {
  auth: {
    all: () => ["auth"] as const,
    user: () => [...queryKeys.auth.all(), "user"] as const,
  },
  profile: {
    all: () => ["profile"] as const,
    detail: () => [...queryKeys.profile.all(), "detail"] as const,
    skills: () => [...queryKeys.profile.all(), "skills"] as const,
    workExperiences: () =>
      [...queryKeys.profile.all(), "work-experiences"] as const,
    accomplishments: (weId?: number) =>
      weId
        ? ([...queryKeys.profile.all(), "accomplishments", weId] as const)
        : ([...queryKeys.profile.all(), "accomplishments"] as const),
    accomplishment: (id: number) =>
      [...queryKeys.profile.all(), "accomplishments", "detail", id] as const,
    educations: () => [...queryKeys.profile.all(), "educations"] as const,
    certifications: () =>
      [...queryKeys.profile.all(), "certifications"] as const,
    stories: () => [...queryKeys.profile.all(), "stories"] as const,
    skillAutocomplete: (q: string) =>
      [...queryKeys.profile.skills(), "autocomplete", q] as const,
  },
  jobs: {
    all: () => ["jobs"] as const,
    list: () => [...queryKeys.jobs.all(), "list"] as const,
    detail: (uuid: string) => [...queryKeys.jobs.all(), uuid] as const,
  },
  generation: {
    all: () => ["generation"] as const,
    templates: () => [...queryKeys.generation.all(), "templates"] as const,
    quota: () => [...queryKeys.generation.all(), "quota"] as const,
    history: (jobUuid: string) =>
      [...queryKeys.generation.all(), "history", jobUuid] as const,
    recent: () => [...queryKeys.generation.all(), "recent"] as const,
    request: (uuid: string) =>
      [...queryKeys.generation.all(), "request", uuid] as const,
    storyRecommendations: (jobUuid: string) =>
      [
        ...queryKeys.generation.all(),
        "story-recommendations",
        jobUuid,
      ] as const,
  },
  subscriptions: {
    all: () => ["subscriptions"] as const,
    status: () => [...queryKeys.subscriptions.all(), "status"] as const,
  },
  tracker: {
    all: () => ["tracker"] as const,
    opportunities: (params?: object) =>
      [...queryKeys.tracker.all(), "opportunities", params ?? {}] as const,
    opportunity: (uuid: string) =>
      [...queryKeys.tracker.all(), "opportunities", uuid] as const,
    companies: () => [...queryKeys.tracker.all(), "companies"] as const,
    company: (uuid: string) =>
      [...queryKeys.tracker.companies(), uuid] as const,
    people: () => [...queryKeys.tracker.all(), "people"] as const,
    person: (uuid: string) => [...queryKeys.tracker.people(), uuid] as const,
    activities: (params?: object) =>
      [...queryKeys.tracker.all(), "activities", params ?? {}] as const,
    tasks: (params?: object) =>
      [...queryKeys.tracker.all(), "tasks", params ?? {}] as const,
    today: () => [...queryKeys.tracker.all(), "today"] as const,
    review: () => [...queryKeys.tracker.all(), "review"] as const,
    icalToken: () => [...queryKeys.tracker.all(), "ical-token"] as const,
    documents: (type?: string) =>
      [...queryKeys.tracker.all(), "documents", type ?? "all"] as const,
    opportunityDocuments: (oppUuid: string) =>
      [
        ...queryKeys.tracker.all(),
        "documents",
        "opportunity",
        oppUuid,
      ] as const,
    industries: () => [...queryKeys.tracker.all(), "industries"] as const,
    groups: () => [...queryKeys.tracker.all(), "groups"] as const,
    group: (uuid: string) => [...queryKeys.tracker.groups(), uuid] as const,
    enrichmentQuota: () =>
      [...queryKeys.tracker.all(), "enrichment-quota"] as const,
    duplicateCompanies: () =>
      [...queryKeys.tracker.all(), "duplicate-companies"] as const,
    duplicatePeople: () =>
      [...queryKeys.tracker.all(), "duplicate-people"] as const,
    imports: () => [...queryKeys.tracker.all(), "imports"] as const,
    import: (uuid: string) =>
      [...queryKeys.tracker.all(), "imports", uuid] as const,
  },
  inbound: {
    all: () => ["inbound"] as const,
    list: () => [...queryKeys.inbound.all(), "list"] as const,
  },
  intelligence: {
    all: () => ["intelligence"] as const,
    emailPreferences: () =>
      [...queryKeys.intelligence.all(), "email-preferences"] as const,
    skillSuggestions: () =>
      [...queryKeys.intelligence.all(), "skill-suggestions"] as const,
  },
  coaching: {
    all: () => ["coaching"] as const,
    sessions: (params?: object) =>
      [...queryKeys.coaching.all(), "sessions", params ?? {}] as const,
    session: (uuid: string) =>
      [...queryKeys.coaching.all(), "sessions", uuid] as const,
    questions: (sessionUuid: string) =>
      [
        ...queryKeys.coaching.all(),
        "sessions",
        sessionUuid,
        "questions",
      ] as const,
    summary: (sessionUuid: string) =>
      [
        ...queryKeys.coaching.all(),
        "sessions",
        sessionUuid,
        "summary",
      ] as const,
    dashboardStatus: () =>
      [...queryKeys.coaching.all(), "dashboard-status"] as const,
    pendingEdits: (sessionUuid?: string) =>
      [
        ...queryKeys.coaching.all(),
        "pending-edits",
        sessionUuid ?? "all",
      ] as const,
  },
  featureFlags: {
    all: () => ["featureFlags"] as const,
    forUser: () => [...queryKeys.featureFlags.all(), "forUser"] as const,
  },
  interviews: {
    all: () => ["interviews"] as const,
    preps: () => [...queryKeys.interviews.all(), "preps"] as const,
    prep: (uuid: string) =>
      [...queryKeys.interviews.all(), "prep", uuid] as const,
    questions: (prepUuid: string) =>
      [...queryKeys.interviews.all(), "questions", prepUuid] as const,
    answers: (prepUuid: string) =>
      [...queryKeys.interviews.all(), "answers", prepUuid] as const,
    mock: (uuid: string) =>
      [...queryKeys.interviews.all(), "mock", uuid] as const,
    mocks: (prepUuid: string) =>
      [...queryKeys.interviews.all(), "mocks", prepUuid] as const,
    readiness: (prepUuid: string) =>
      [...queryKeys.interviews.all(), "readiness", prepUuid] as const,
  },
  profileOptimizer: {
    all: () => ["profileOptimizer"] as const,
    eligibility: () =>
      [...queryKeys.profileOptimizer.all(), "eligibility"] as const,
    targetRoles: () =>
      [...queryKeys.profileOptimizer.all(), "target-roles"] as const,
    current: () => [...queryKeys.profileOptimizer.all(), "current"] as const,
    status: () => [...queryKeys.profileOptimizer.all(), "status"] as const,
    drift: () => [...queryKeys.profileOptimizer.all(), "drift"] as const,
    history: () => [...queryKeys.profileOptimizer.all(), "history"] as const,
    optimization: (uuid: string) =>
      [...queryKeys.profileOptimizer.all(), "optimization", uuid] as const,
  },
} as const;
